import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Spin,
  message,
  Table,
  DatePicker,
  Space,
  Typography,
  Empty
} from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import governanceService from '@services/operator/governanceService';
import ListPageHeader from '@shared/ListPageHeader';
import { formatDate } from '@utils/dateTime';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const AiUsage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await governanceService.getUsageSummary();
      if (response.success) {
        // 本頁是「AI 用量與費用」，要讀的是 ai_credits_wallet（含 balance / lifetime_* / percentage_used）。
        // 不能讀 data.summary —— 那是 orders/shipments/pcs 的方案配額陣列。
        setSummary(response.data.ai_credits_wallet || {});
      }
    } catch (error) {
      console.error('Failed to fetch usage summary:', error);
    }
  }, []);

  const fetchRecords = useCallback(async (startDate, endDate) => {
    try {
      setLoading(true);
      const response = await governanceService.getUsageRecords({
        start_date: startDate.format('YYYY-MM-DD'),
        end_date: endDate.format('YYYY-MM-DD')
      });

      if (response.success) {
        setRecords(response.data.usage_records || response.data.records || []);
      } else {
        message.error(response.message || t('common.error.fetch_failed'));
      }
    } catch (error) {
      console.error('Failed to fetch usage records:', error);
      message.error(t('common.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSummary();
    fetchRecords(dateRange[0], dateRange[1]);
  }, [fetchSummary, fetchRecords, dateRange]);

  const handleDateChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dates);
    }
  };

  const columns = [
    {
      title: t('operator.governance.ai_usage.date'),
      dataIndex: 'usage_date',
      key: 'usage_date',
      render: (date) => formatDate(date)
    },
    {
      title: t('operator.governance.ai_usage.type'),
      dataIndex: 'usage_type',
      key: 'usage_type'
    },
    {
      title: t('operator.governance.ai_usage.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right'
    },
    {
      title: t('operator.governance.ai_usage.unit_price'),
      dataIndex: 'unit_price',
      key: 'unit_price',
      align: 'right',
      render: (price, record) => price != null ? `${record.currency} ${price.toLocaleString()}` : '-'
    },
    {
      title: t('operator.governance.ai_usage.amount'),
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (amount, record) => (
        <Text strong>{record.currency} {amount?.toLocaleString()}</Text>
      )
    }
  ];

  // 後端 build_ai_credits_wallet_summary 已算好 percentage_used = consumed / (granted+purchased) * 100
  const usagePercent = summary?.percentage_used != null
    ? Math.round(summary.percentage_used)
    : 0;

  return (
    <div>
      <ListPageHeader
        items={[
          'governance',
          { label: t('operator.menu.governance.ai_usage'), path: '/governance/ai_usage' }
        ]}
        title={t('operator.governance.ai_usage.title')}
      />

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Card variant="borderless">
              <Statistic
                title={t('operator.governance.ai_usage.balance')}
                value={summary?.balance ?? 0}
                precision={2}
                prefix={summary?.currency}
                valueStyle={{ color: summary?.balance > 0 ? '#3f8600' : '#cf1322' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card variant="borderless">
              <Statistic
                title={t('operator.governance.ai_usage.total_consumed')}
                value={summary?.lifetime_consumed ?? 0}
                precision={2}
                prefix={summary?.currency}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card variant="borderless">
              <Statistic
                title={t('operator.governance.ai_usage.remaining')}
                value={summary?.remaining ?? summary?.balance ?? 0}
                precision={2}
                prefix={summary?.currency}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card variant="borderless">
              <Statistic
                title={t('operator.governance.ai_usage.usage_ratio')}
                value={usagePercent}
                suffix="%"
                valueStyle={{ color: usagePercent > 90 ? '#cf1322' : undefined }}
              />
            </Card>
          </Col>
        </Row>

        <Card
          title={t('operator.governance.ai_usage.records_title')}
          extra={
            <Space>
              <Text>{t('operator.governance.ai_usage.date_range')}:</Text>
              <RangePicker
                value={dateRange}
                onChange={handleDateChange}
                allowClear={false}
              />
            </Space>
          }
        >
          {loading ? (
            <div style={{ padding: 60, textAlign: 'center' }}>
              <Spin />
            </div>
          ) : records.length > 0 ? (
            <Table
              dataSource={records}
              columns={columns}
              rowKey="id"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => t('common.pagination.total', { total })
              }}
              size="small"
            />
          ) : (
            <Empty description={t('operator.governance.ai_usage.no_records')} />
          )}
        </Card>
      </Space>
    </div>
  );
};

export default AiUsage;
